import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  LocalTrack,
  RemoteTrack,
  Room,
  TrackPublication,
} from 'livekit-client';
import {
  couperLeFluxDUnParticipant,
  obtenirLeDroitDEntreeAuCanal,
  signalerUneChuteDeCanal,
} from '@groupe-trousseau/shared/api-client';
import { fluxDiffuses, type FluxPublie } from '@groupe-trousseau/shared/lib';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE SEUL FICHIER DE `web/` QUI CONNAÎT `livekit-client` — D-003.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Il le charge à la demande depuis le 20260830**, carte #23 : les valeurs du SDK
 * arrivent par `await import(…)`, et seuls ses **types** sont importés statiquement —
 * `import type` étant effacé à la compilation, il ne produit aucune ligne de paquet.
 * Le motif et les deux formes que la sonde doit tenir sont écrits plus bas, sur
 * `chargerLeSdk`.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « La visio est isolée derrière une interface de cinq méthodes, rejoindre, quitter,
 * **publier**, **s'abonner**, enregistrer, **sans appel direct au SDK ailleurs**, pour
 * que la bascule self-host se fasse par trois variables d'environnement. » Les deux
 * méthodes en gras sont celles qui vivent ici ; les autres sont dans
 * `api/src/live/visio.service.ts`, et `enregistrer` est hors V1 (cadrage §3.2).
 * **`web/e2e/port-de-la-visio.spec.ts` vérifie par balayage que ce fichier reste le
 * seul**, et il balaye **les deux formes** depuis le découpage à la demande : la ligne
 * d'import et l'appel `import('livekit-client')`, qui ne s'écrit pas dans une ligne
 * d'import et qu'un balayage des imports ne verrait pas.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUN TYPE DU SDK NE SORT D'ICI, ET C'EST CE QUI REND LE PORT VRAI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Une vignette n'expose pas la piste du fournisseur : elle expose **`brancher`**, une
 * fonction qui pose le flux sur un élément vidéo et rend de quoi le retirer. C'est ce
 * qui permet à un composant de peindre une image **sans jamais nommer la
 * bibliothèque** — sans quoi le port serait un port de nom seulement, chaque écran
 * important `RemoteVideoTrack` pour appeler `attach`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **AUCUNE ADRESSE DE FOURNISSEUR N'EST DÉCLARÉE DANS CE FRONT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * L'adresse du canal **arrive avec le droit d'entrée**, rendue par le serveur. Il n'y a
 * donc pas de `VITE_VISIO_URL` : D-003 pose que la bascule self-host se fait par
 * **trois** variables d'environnement, et une quatrième posée dans la construction du
 * front en ferait quatre — dont une qu'on oublierait le jour de la bascule. C'est aussi
 * la leçon de D-092, où une variable de front nommait un domaine que rien ne servait.
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE SDK EST CHARGÉ À LA DEMANDE, ET LE PORT DE D-003 EN SORT PLUS ÉTROIT.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le poids du front de séance doublait avec ce client** — 405 ko avant, 925 ko après,
 * mesuré à D-113 — et **un cours sans visio payait le prix comme les autres** : le
 * paquet servi porte le SDK dès la première ouverture de l'écran, qu'un canal se monte
 * ou non. Or `visioDisponible` peut valoir faux, une structure peut ne pas avoir de
 * visio, et le corpus dit que « le cours continue sur le tableau, et **le tableau ne
 * dépend pas de la visio** ».
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE DÉCOUPAGE NE DESSERRE PAS LA BORNE DE D-003, IL LA RESSERRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * D-003 pose « **sans appel direct au SDK ailleurs** ». Avant, un fichier tiers pouvait
 * casser la règle **d'une seule façon** : `import … from 'livekit-client'`. Il en a
 * désormais **deux** — la même, et `await import('livekit-client')`, qui ne s'écrit pas
 * dans une ligne d'import et **qu'un balayage des imports ne voit pas**.
 * `web/e2e/port-de-la-visio.spec.ts` balaye donc les deux formes, et il vérifie en plus
 * que **ce fichier-ci n'importe le SDK que dynamiquement** : sans cette seconde
 * affirmation, un import statique réintroduit ici ferait doubler le paquet servi **sans
 * qu'aucune commande ne rougisse**, le port restant unique et la règle de D-003 restant
 * tenue. **Le gain du découpage est invisible ; c'est exactement ce qu'une sonde doit
 * tenir.**
 *
 * **Les TYPES, eux, restent en import statique**, et ce n'est pas une entorse :
 * `import type` est **effacé à la compilation** et ne produit aucune ligne de paquet.
 * C'est ce qui permet à `Room`, `Track` et les trois types de piste de continuer à
 * typer ce fichier sans que le SDK entre dans le paquet d'entrée.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE CHARGEMENT PEUT ÉCHOUER, ET C'EST DÉJÀ UN CAS DU CORPUS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Un paquet servi à la demande est une requête réseau de plus, donc **une panne de
 * plus** : réseau coupé, déploiement en cours, paquet évincé du cache. DD-18 la nomme
 * déjà — « **indisponible au démarrage** », distinct de « la chute » —, et le produit y
 * répond déjà : `demarrageEchoue`, le panneau reste et dit ce qui manque, **aucune ligne
 * de chute n'est écrite**. Le SDK qui ne se charge pas emprunte donc ce chemin-là plutôt
 * que d'en ouvrir un neuf. **Et il ne touche pas le tableau** : l'effet rend la main, et
 * rien d'autre de l'écran n'attend ce paquet.
 */
type ModuleDeVisio = typeof import('livekit-client');

/**
 * **Une seule promesse pour tout l'onglet**, et c'est ce qui rend le rechargement
 * gratuit. Sans elle, deux montages successifs du même écran — un `StrictMode`, une
 * circulation aller-retour, un rétablissement après chute — relanceraient l'import.
 * Le navigateur servirait le paquet depuis son cache, mais **l'attente reviendrait**,
 * et le canal se remonterait un tour de boucle plus tard qu'il ne le doit.
 */
let modulePromesse: Promise<ModuleDeVisio> | null = null;

function chargerLeSdk(): Promise<ModuleDeVisio> {
  // **Une promesse rejetée ne se garde pas.** La retenir ferait échouer instantanément
  // toutes les tentatives suivantes, et DD-18 veut qu'on « retente, tant que le cours
  // vit ». Un rétablissement après une coupure réseau doit pouvoir recharger.
  modulePromesse ??= import('livekit-client').catch((raison: unknown) => {
    modulePromesse = null;
    throw raison;
  });
  return modulePromesse;
}

/** D-004 — « publication plafonnée à 360p et 20 images par seconde ». */
const plafondDePublication = (sdk: ModuleDeVisio) => sdk.VideoPresets.h360;

/**
 * **Le droit vaut dix minutes et se redemande** (Q433). On le redemande **une minute
 * avant** son terme : un droit périmé ne coupe pas un canal déjà monté, mais il est ce
 * dont un retour de coupure a besoin, et c'est exactement le moment où on ne peut pas
 * aller en chercher un.
 */
const MARGE_DE_RENOUVELLEMENT_S = 60;

/**
 * **Ce que le produit dit à trente secondes de coupure** (DD-18).
 *
 * Exporté parce que **la page écrit la phrase et cette feature tient le seuil** : le
 * recopier dans l'écran ferait deux vérités pour un nombre du corpus.
 */
export const SEUIL_DE_MESSAGE_S = 30;

/** Entre deux tentatives de rétablissement. « Tant que le cours vit », sans compte. */
const ATTENTE_DE_RETABLISSEMENT_MS = 5_000;

/**
 * **La préférence de micro et de caméra, passée par la PAGE et non lue ici.**
 *
 * « Le produit conserve une préférence de micro et de caméra par personne, appliquée à
 * chaque entrée », et « couper ou rallumer pendant un cours met à jour cette
 * préférence » (Q419, Q420). Elle vit donc dans les réglages, **qui sont une autre
 * feature**.
 *
 * **Ce n'est pas ce hook qui va la chercher, et la raison est une barrière de lint** :
 * « une feature n'atteint QUE la sienne, plus les primitives », `DOCTRINE.md` §10 encodé
 * dans `eslint.config.mjs`. La première version de ce fichier importait
 * `features/reglages/reglages`, et **le lint l'a refusée** — la façade d'une autre
 * feature n'est pas plus permise que son intérieur. C'est la page qui compose les deux,
 * et c'est ce que `pages/` est.
 *
 * **Le repli de la page est Q563** : « à la toute première entrée, le micro est allumé
 * ET la caméra est allumée », renversement assumé par le porteur avec son coût écrit.
 * Un repli plus prudent ferait de ce front une seconde vérité, plus timide que la règle
 * et donc fausse.
 */
export type PreferenceDeMedia = {
  readonly micro: boolean;
  readonly camera: boolean;
  /** Appelé quand la personne coupe ou rallume elle-même, et alors seulement. */
  readonly onBascule: (flux: 'micro' | 'camera', ouvert: boolean) => void;
};

/** Un flux d'image à peindre. **Aucun type du SDK n'y paraît.** */
export type VignetteDeVisio = {
  /** Stable pour une même piste : sert de clé de rendu. */
  readonly cle: string;
  /** La personne du produit, ou vide quand la correspondance manque encore. */
  readonly personneId: string | null;
  /** Vrai pour un écran partagé, DD-17 : le produit ne le retire jamais du seuil. */
  readonly estUnEcranPartage: boolean;
  /** Vrai quand c'est son propre flux : il se peint en miroir et sans son. */
  readonly estLeMien: boolean;
  /** Branche le flux sur un élément, et rend de quoi le débrancher. */
  readonly brancher: (element: HTMLMediaElement) => () => void;
};

/** Un flux de son. **Il n'est jamais filtré** : « les autres restent audibles ». */
export type SonDeVisio = {
  readonly cle: string;
  readonly brancher: (element: HTMLMediaElement) => () => void;
};

export type EtatDeLaVisio = {
  /**
   * ═════════════════════════════════════════════════════════════════════════
   * **VIDE TANT QU'ON NE SAIT PAS, FAUX QUAND LE COURS N'A PAS DE CANAL — ET
   * CE CHAMP NE DIT RIEN DE LA LIAISON.**
   * ═════════════════════════════════════════════════════════════════════════
   *
   * « Si le service extérieur est indisponible au démarrage, le cours démarre quand
   * même, sans visio » (DD-18). **Ce n'est pas une erreur, c'est un mode de marche**,
   * et l'écran le dit sans rien proposer : le cours continue sur le tableau.
   *
   * **Il est DÉRIVÉ de la réponse du droit et n'est jamais rangé dans l'état**, pour
   * deux raisons qui vont dans le même sens. `web/CLAUDE.md` interdit « toute donnée
   * serveur recopiée dans un store client », et la barrière de lint
   * `react-hooks/set-state-in-effect` refuse de le poser depuis l'effet — **elle a
   * refusé la première version de ce fichier**. Une valeur du serveur recopiée dans un
   * état local est une seconde vérité qui se désynchronise ; celle-ci s'était
   * désynchronisée dans le pire sens, en restant vide quand le canal existait.
   *
   * **L'état de la LIAISON vit ailleurs**, dans `reliee`, `demarrageEchoue` et
   * `coupeDepuisSecondes` : trois choses différentes, et les confondre avec celle-ci
   * retirait le panneau entier.
   */
  readonly visioDisponible: boolean | null;
  /** Vrai quand le canal est monté. */
  readonly reliee: boolean;
  /** Depuis quand ce poste ne voit plus le canal, en secondes, ou vide. */
  readonly coupeDepuisSecondes: number | null;
  /** Les flux d'image à peindre, seuil de six appliqué. */
  readonly vignettes: readonly VignetteDeVisio[];
  /** Les flux de son, jamais filtrés. */
  readonly sons: readonly SonDeVisio[];
  /**
   * **Faux quand mon image n'est plus diffusée par le régime dégradé** (Q417).
   *
   * « Le produit dit à la personne que son image n'est plus diffusée et qu'elle le sera
   * quand elle parlera. » Vrai aussi quand je ne publie aucune image : il n'y a alors
   * rien à annoncer.
   */
  /**
   * **Vrai quand le canal existe et que ce poste n'a JAMAIS réussi à le joindre.**
   *
   * **Ce n'est pas une chute, et les confondre était le défaut** trouvé au navigateur le
   * 20260827 : DD-18 sépare les deux cas et leur donne deux phrases. « Si le service
   * extérieur est **indisponible au démarrage**, le cours démarre quand même, sans
   * visio » — rien n'est tombé, rien ne se met au relevé des chutes. « **La chute** »,
   * elle, part d'une liaison établie, et « chaque chute laisse une ligne » (Q421).
   */
  readonly demarrageEchoue: boolean;
  readonly monImageEstDiffusee: boolean;
  readonly microOuvert: boolean;
  readonly cameraAllumee: boolean;
  readonly ecranPartage: boolean;
  /**
   * **Vrai quand le navigateur a refusé ou n'a pas trouvé la caméra ou le micro.**
   *
   * « Une caméra ou un micro absents, en panne, ou refusés par le navigateur
   * **n'empêchent jamais d'entrer** » (DD-15) : c'est donc un état à dire, pas une
   * erreur à traiter.
   */
  readonly materielRefuse: boolean;
};

/** L'état vraiment tenu ici : tout sauf ce qui se dérive de la réponse du droit. */
type EtatTenu = Omit<EtatDeLaVisio, 'visioDisponible'>;

const ETAT_INITIAL: EtatTenu = {
  reliee: false,
  coupeDepuisSecondes: null,
  vignettes: [],
  sons: [],
  demarrageEchoue: false,
  monImageEstDiffusee: true,
  microOuvert: false,
  cameraAllumee: false,
  ecranPartage: false,
  materielRefuse: false,
};

/**
 * **F-31 — le droit d'entrée dans le canal, et son renouvellement.**
 *
 * **C'est une lecture et non un geste, malgré son verbe HTTP** : « il n'est pas à usage
 * unique, contrairement au lien d'auto-connexion — il se redemande tant que la personne
 * est présente » (Q433). Le redemander ne change rien à l'état du produit, donc il vit
 * dans une requête qui se rafraîchit et non dans une mutation qu'on déclenche.
 *
 * **La cadence se dérive de la réponse et n'est pas écrite en dur** : le serveur dit
 * combien de temps son droit vaut, et le front le redemande une minute avant. Recopier
 * dix minutes ici créerait une seconde vérité, qui divergerait le jour où Q433 bouge.
 */
function useDroitDEntree(coursId: string | null, actif: boolean) {
  return useQuery({
    queryKey: CLES.cours.canal(coursId ?? ''),
    queryFn: () =>
      deballer(obtenirLeDroitDEntreeAuCanal({ path: { id: coursId ?? '' } })),
    enabled: actif && coursId !== null,
    refetchInterval: (requete) => {
      const duree = requete.state.data?.expireDansSecondes ?? 0;
      if (duree <= 0) return false;
      return Math.max(duree - MARGE_DE_RENOUVELLEMENT_S, MARGE_DE_RENOUVELLEMENT_S) * 1_000;
    },
    // **Un droit ne se relit pas au retour d'onglet** : il vaut dix minutes, et le
    // rafraîchissement ci-dessus le tient. Le relire à chaque focus ferait un appel
    // par aller-retour entre deux fenêtres, pendant tout le cours.
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * **F-31 — la chute du canal, rapportée par le poste et par personne d'autre.**
 *
 * « **Le service extérieur n'appelle jamais le produit** » (Q432) : la ligne s'écrit
 * « sur ce que rapporte le poste qui revient, par un appel ordinaire d'une personne
 * connectée ». **Chaque chute laisse une ligne** (Q421), et **aucun email ne part**
 * (DD-18).
 *
 * **`retry` est posé, et c'est la conséquence de Q432** : une chute de visio arrive
 * souvent avec un réseau abîmé, donc le premier appel peut échouer lui aussi. Sans
 * reprise, « chaque chute laisse une ligne » serait vrai des chutes propres et faux de
 * celles qui comptent.
 */
function useSignalerUneChute(coursId: string | null) {
  return useMutation({
    mutationFn: () =>
      deballer(signalerUneChuteDeCanal({ path: { id: coursId ?? '' } })),
    retry: 3,
    retryDelay: (tentative) => Math.min(2_000 * 2 ** tentative, 30_000),
  });
}

/**
 * **F-31 et F-32 — le tuteur coupe le micro, la caméra ou le partage d'un élève.**
 *
 * **Il n'existe aucun hook symétrique, et c'est la règle et non un manque** : « personne
 * ne peut allumer le flux de quelqu'un d'autre, jamais, pas même le tuteur. Couper
 * retire, allumer expose » (DD-16). Le contrat n'a pas de route pour allumer.
 *
 * **La réponse porte `coupe`, et l'écran doit le lire** : faux quand le service
 * extérieur n'a pas répondu. Un écran qui annoncerait la coupure sans regarder serait
 * « un écran qui a l'air de marcher ».
 */
export function useCouperLeFlux(coursId: string) {
  return useMutation({
    mutationFn: (params: {
      readonly personneId: string;
      readonly flux: 'micro' | 'camera' | 'ecran';
    }) =>
      deballer(
        couperLeFluxDUnParticipant({
          path: { id: coursId, personneId: params.personneId },
          body: { flux: params.flux },
        }),
      ),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE CANAL DE VISIO D'UN COURS — F-31, F-32.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **« Entrer dans un cours, c'est entrer dans son canal. Aucun geste séparé, aucun écran
 * d'attente » (DD-15).** Ce hook n'a donc pas de fonction « rejoindre » : il se monte
 * avec l'écran du cours, et l'entrée est son effet.
 *
 * **CINQ RÈGLES DE F-31 SONT TENUES ICI, ET AUCUNE NE SE DEVINE À LA LECTURE DU CODE
 * SEUL.**
 *
 * **1. La préférence de micro et de caméra s'applique sans rien demander** (Q419, Q420,
 * critère 1). Elle est lue au réglage de la personne, et **couper ou rallumer pendant le
 * cours la met à jour** — donc le geste de cet écran écrit dans les réglages. « À la
 * toute première entrée, le micro est allumé ET la caméra est allumée » (Q563) : c'est
 * la valeur par défaut du modèle, et ce front ne la redécide pas.
 *
 * **2. Une coupure imposée par le tuteur ne met PAS la préférence à jour** (DD-16). Elle
 * arrive par le fournisseur, pas par un geste de cet écran, donc elle ne passe pas par
 * la fonction qui écrit — et « elle peut se rallumer immédiatement ».
 *
 * **3. Le régime dégradé est une règle PARTAGÉE, et ce front la rejoue** : `fluxDiffuses`
 * vit dans `shared/lib` et « le front la rejoue parce que c'est lui qui décide quoi
 * peindre ». **Chaque poste calcule le même résultat des mêmes entrées**, donc chacun
 * sait aussi si **sa propre** image est diffusée — ce qui est ce que Q417 demande
 * d'annoncer.
 *
 * **4. On ne montre rien de la liaison des autres** (DD-07) : aucun voyant, aucune
 * qualité, aucune cause. « Les autres voient qu'elle n'a plus ni image ni son, **sans
 * savoir pourquoi** » (DD-18). Ce hook n'expose donc aucun état de liaison distant.
 *
 * **5. Un écran partagé se substitue à l'image, il ne s'y ajoute pas** (Q423). Partager
 * coupe la caméra, cesser de partager la rétablit selon la préférence — et « le nombre
 * de flux publiés ne change donc pas quand quelqu'un partage ».
 */
export function useVisioDuCours(
  coursId: string | null,
  actif: boolean,
  preference: PreferenceDeMedia,
) {
  const droit = useDroitDEntree(coursId, actif);
  const chute = useSignalerUneChute(coursId);

  const [etat, poserLEtat] = useState<EtatTenu>(ETAT_INITIAL);
  const salle = useRef<Room | null>(null);
  /** Le module chargé, posé par l'effet et lu par les rappels. Voir `chargerLeSdk`. */
  const moduleCharge = useRef<ModuleDeVisio | null>(null);
  const jetonCourant = useRef<string | null>(null);

  const jeton = droit.data?.jeton ?? null;
  const adresse = droit.data?.adresseDuCanal ?? null;
  const visioDisponible = droit.data?.visioDisponible ?? null;

  /** La correspondance opaque → personne, Q431. Vide avant le premier droit. */
  const correspondances = useMemo(() => {
    const table = new Map<string, string>();
    for (const ligne of droit.data?.correspondances ?? []) {
      table.set(ligne.identite, ligne.personneId);
    }
    return table;
  }, [droit.data]);

  // Le jeton le plus frais reste sous la main : c'est lui dont un rétablissement a
  // besoin, et il change sans que le canal ait à se remonter.
  useEffect(() => {
    jetonCourant.current = jeton;
  }, [jeton]);

  const preferenceMicro = preference.micro;
  const preferenceCamera = preference.camera;

  /**
   * **Ce que l'écran lit se recalcule à chaque événement du fournisseur.**
   *
   * Une seule fonction, appelée par tous les écouteurs : la liste des flux, le seuil de
   * six, et l'annonce de sa propre image sortent tous du **même** état de la salle. Les
   * calculer à des endroits différents les ferait diverger entre deux événements.
   */
  const relire = useCallback(
    (piece: Room) => {
      // **Le SDK se lit sur une référence, exactement comme la salle, et pour le même
      // motif** : les deux naissent dans l'effet et sont lus par des rappels qui vivent
      // plus longtemps que lui. Ses deux énumérations — la source d'une piste, son
      // genre — ne sont plus lisibles au module depuis que le chargement est différé.
      //
      // **Les remplacer par les chaînes qu'elles valent aujourd'hui aurait été le geste
      // facile et le mauvais** : une valeur d'énumération du fournisseur recopiée en
      // littéral **cesse d'être vérifiée par le compilateur**, et une montée de majeure
      // qui la renomme casserait l'affichage des vignettes en silence.
      const sdk = moduleCharge.current;
      // Injoignable en pratique — la salle n'existe pas avant le module — et gardé
      // plutôt que forcé : un `!` ici mentirait sur ce que le type sait.
      if (sdk === null) return;
      const { Track } = sdk;
      const moi = piece.localParticipant;
      const tous = [moi, ...piece.remoteParticipants.values()];

      const publies: FluxPublie[] = [];
      const vignettes: VignetteDeVisio[] = [];
      const sons: SonDeVisio[] = [];

      for (const personne of tous) {
        const estLeMien = personne.identity === moi.identity;
        const personneId = correspondances.get(personne.identity) ?? null;

        for (const publication of personne.trackPublications.values()) {
          const piste = publication.track;
          if (piste === undefined || piste === null) continue;
          if (publication.isMuted) continue;

          const estUnEcranPartage = publication.source === Track.Source.ScreenShare;
          const estUneImage =
            publication.kind === Track.Kind.Video &&
            (estUnEcranPartage || publication.source === Track.Source.Camera);

          if (estUneImage) {
            publies.push({
              // **Le seuil compte des FLUX, pas des personnes** : une personne qui
              // partage son écran alors que sa caméra est coupée pèse un flux, et le
              // corpus dit que le partage « se substitue à l'image ».
              personneId: personneId ?? personne.identity,
              estUnEcranPartage,
              parle: personne.isSpeaking,
            });
            vignettes.push({
              cle: publication.trackSid,
              personneId,
              estUnEcranPartage,
              estLeMien,
              brancher: brancheur(piste),
            });
            continue;
          }

          // **Le son de son propre poste ne se rebranche jamais** : on s'entendrait
          // avec le délai du réseau. Et le son d'un écran partagé compte comme du son.
          if (publication.kind === Track.Kind.Audio && !estLeMien) {
            sons.push({ cle: publication.trackSid, brancher: brancheur(piste) });
          }
        }
      }

      const diffuses = new Set(fluxDiffuses(publies));
      const monIdentifiant =
        correspondances.get(moi.identity) ?? moi.identity;
      const jePublieUneImage = publies.some(
        (flux) => flux.personneId === monIdentifiant,
      );

      poserLEtat((precedent) => ({
        ...precedent,
        reliee: true,
        coupeDepuisSecondes: null,
        // Le seuil retire des flux de l'affichage, jamais du son.
        vignettes: vignettes.filter((vignette) =>
          diffuses.has(vignette.personneId ?? ''),
        ),
        sons,
        monImageEstDiffusee: !jePublieUneImage || diffuses.has(monIdentifiant),
        microOuvert: moi.isMicrophoneEnabled,
        cameraAllumee: moi.isCameraEnabled,
        ecranPartage: moi.isScreenShareEnabled,
      }));
    },
    [correspondances],
  );

  /**
   * **Le canal se monte une fois par cours, et le renouvellement du droit ne le
   * remonte pas.**
   *
   * L'effet ne dépend **ni du jeton ni des correspondances** : les mettre en dépendance
   * couperait la visio de tout le monde toutes les neuf minutes, au renouvellement.
   * C'est le défaut exact de D-110 dans l'autre sens — une valeur qui change souvent
   * employée comme déclencheur d'un geste qui doit être rare.
   */
  useEffect(() => {
    if (!actif || coursId === null) return;
    if (visioDisponible !== true || adresse === null || jetonCourant.current === null) {
      return;
    }

    let vivant = true;
    /**
     * **La salle et le battement naissent APRÈS une attente, donc le nettoyage doit
     * savoir qu'ils peuvent ne pas exister.** C'est la seule chose que le chargement
     * différé change à la forme de cet effet : quitter l'écran pendant que le paquet
     * arrive est un cas ordinaire, pas un cas de bord.
     */
    let piece: Room | null = null;
    let battement: ReturnType<typeof setInterval> | null = null;
    let chuteRapportee = false;
    let coupeeA: number | null = null;
    /**
     * **Faux jusqu'à la première liaison établie, et c'est ce qui sépare la chute du
     * démarrage manqué.** Sans ce drapeau, une visio qui ne monte jamais écrivait une
     * ligne au relevé des chutes — mesuré, `ChannelDropEntry` passait de 1 à 2 sur une
     * sonde où rien ne s'était jamais connecté. « Chaque chute laisse une ligne » ne
     * dit pas « chaque échec laisse une ligne », et le relevé sert les agrégats du
     * compte qui voit tout : le gonfler de faux le rend inutilisable.
     */
    let aEteReliee = false;

    void (async () => {
      let sdk: ModuleDeVisio;
      try {
        sdk = await chargerLeSdk();
      } catch {
        // **DD-18, « indisponible au démarrage », et rien de neuf.** Un paquet qui
        // n'arrive pas est une visio qui ne monte pas : le panneau reste et dit ce qui
        // manque, **aucune ligne de chute n'est écrite** — « chaque chute laisse une
        // ligne » ne dit pas « chaque échec laisse une ligne » (D-113). Et le tableau
        // continue : rien d'autre de l'écran n'attend ce paquet.
        if (vivant) {
          poserLEtat((precedent) => ({
            ...precedent,
            reliee: false,
            demarrageEchoue: true,
          }));
        }
        return;
      }
      // **Quitter l'écran pendant le chargement ne monte aucun canal.** Sans ce retour,
      // une circulation rapide laisserait une salle connectée que le nettoyage, déjà
      // passé, ne fermerait jamais — un canal orphelin par onglet.
      if (!vivant) return;
      moduleCharge.current = sdk;
      const { DisconnectReason, Room: Salle, RoomEvent } = sdk;
      const PLAFOND_DE_PUBLICATION = plafondDePublication(sdk);

      const montee = new Salle({
        // D-004 : « publication plafonnée à 360p et 20 images par seconde, dynacast et
        // adaptive stream activés. La latence du trait se mesure toujours avec la visio
        // allumée. »
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: PLAFOND_DE_PUBLICATION.resolution },
        publishDefaults: { videoEncoding: PLAFOND_DE_PUBLICATION.encoding },
      });
      piece = montee;
      salle.current = montee;

      const surChangement = () => {
        if (vivant) relire(montee);
      };

      // ── `s'abonner` ──
      montee.on(RoomEvent.TrackSubscribed, surChangement);
      montee.on(RoomEvent.TrackUnsubscribed, surChangement);
      montee.on(RoomEvent.LocalTrackPublished, surChangement);
      montee.on(RoomEvent.LocalTrackUnpublished, surChangement);
      montee.on(RoomEvent.TrackMuted, surChangement);
      montee.on(RoomEvent.TrackUnmuted, surChangement);
      montee.on(RoomEvent.ParticipantConnected, surChangement);
      montee.on(RoomEvent.ParticipantDisconnected, surChangement);
      // **Le régime dégradé suit la parole**, donc il se recalcule à chaque changement de
      // qui parle : c'est ce qui le rend « automatique et réversible » (Q417).
      montee.on(RoomEvent.ActiveSpeakersChanged, surChangement);

      /**
       * **La chute, et les deux choses qu'elle déclenche.**
       *
       * Elle **rapporte une ligne** — « chaque chute laisse une ligne » (Q421) —, et elle
       * **retente, tant que le cours vit, sans nombre de tentatives écrit** (DD-18).
       *
       * **Une sortie voulue n'est pas une chute** : quitter l'écran ferme le canal, et
       * écrire une ligne de chute à chaque fin de cours remplirait le relevé de faux.
       */
      montee.on(RoomEvent.Disconnected, (raison) => {
        if (!vivant) return;
        if (raison === DisconnectReason.CLIENT_INITIATED) return;

        coupeeA = Date.now();
        poserLEtat((precedent) => ({
          ...precedent,
          reliee: false,
          coupeDepuisSecondes: 0,
          // **Les vignettes des autres disparaissent** : les laisser figées ferait croire
          // que le cours continue en image alors qu'on ne reçoit plus rien.
          vignettes: [],
          sons: [],
        }));

        if (!aEteReliee) {
          // **Rien n'était monté : ce n'est pas une chute**, c'est DD-18 « indisponible
          // au démarrage ». Aucune ligne, et la phrase de l'écran est l'autre.
          poserLEtat((precedent) => ({
            ...precedent,
            coupeDepuisSecondes: null,
            demarrageEchoue: true,
          }));
          coupeeA = null;
        } else if (!chuteRapportee) {
          chuteRapportee = true;
          chute.mutate();
        }

        const retenter = () => {
          if (!vivant || jetonCourant.current === null) return;
          void montee
            .connect(adresse, jetonCourant.current)
            .then(() => {
              aEteReliee = true;
              chuteRapportee = false;
              coupeeA = null;
              poserLEtat((precedent) => ({
                ...precedent,
                demarrageEchoue: false,
              }));
              relire(montee);
            })
            .catch(() => {
              if (vivant) setTimeout(retenter, ATTENTE_DE_RETABLISSEMENT_MS);
            });
        };
        setTimeout(retenter, ATTENTE_DE_RETABLISSEMENT_MS);
      });

      // « En deçà de trente secondes, rien ne se passe » : ce qui est montré est le
      // compteur, et à trente secondes la phrase de DD-18.
      battement = setInterval(() => {
        if (coupeeA === null) return;
        poserLEtat((precedent) => ({
          ...precedent,
          coupeDepuisSecondes: Math.round((Date.now() - coupeeA!) / 1_000),
        }));
      }, 1_000);

      // ── `rejoindre`, puis `publier` selon la préférence ──
      //
      // **Le jeton se relit ICI et non au sommet de l'effet**, et le chargement différé
      // rend ce détail réel : le paquet peut mettre plus longtemps à arriver que le droit
      // à se renouveler. Prendre celui du sommet ferait entrer avec un jeton périmé — un
      // refus à l'entrée sur un chemin qui marchait avant le découpage.
      const jetonDEntree = jetonCourant.current;
      if (jetonDEntree === null) return;

      void montee
        .connect(adresse, jetonDEntree)
        .then(async () => {
          if (!vivant) return;
          aEteReliee = true;
          poserLEtat((precedent) => ({
            ...precedent,
            reliee: true,
            demarrageEchoue: false,
          }));
          try {
            // **La préférence s'applique sans rien demander** (critère 1 de F-31).
            await montee.localParticipant.setMicrophoneEnabled(preferenceMicro);
            await montee.localParticipant.setCameraEnabled(preferenceCamera);
          } catch {
            // « Une caméra ou un micro absents, en panne, ou refusés par le navigateur
            // **n'empêchent jamais d'entrer** » (DD-15). On entre, et on le dit.
            if (vivant) {
              poserLEtat((precedent) => ({ ...precedent, materielRefuse: true }));
            }
          }
          if (vivant) relire(montee);
        })
        .catch(() => {
          // Un canal qui ne monte pas laisse le cours vivre : « le cours continue sur le
          // tableau, et le tableau ne dépend pas de la visio ». **Le panneau reste**, et
          // il dit ce qui manque — c'est le correctif du 20260827.
          if (vivant) {
            poserLEtat((precedent) => ({
              ...precedent,
              reliee: false,
              demarrageEchoue: true,
            }));
          }
        });
    })();

    return () => {
      // ── `quitter` ──
      //
      // **`vivant` est posé AVANT tout le reste, et il est la seule chose que ce
      // nettoyage puisse faire quand le paquet n'est pas encore arrivé.** Le montage
      // qui attend le lira au retour de son `await` et rendra la main sans rien
      // connecter — sans quoi une circulation rapide laisserait un canal ouvert que
      // personne ne referme, un par onglet.
      vivant = false;
      if (battement !== null) clearInterval(battement);
      if (piece !== null) {
        piece.removeAllListeners();
        void piece.disconnect();
      }
      salle.current = null;
      // **Le module chargé, lui, ne se relâche pas** : c'est du code, pas une liaison.
      // `modulePromesse` le garde pour tout l'onglet, et le prochain montage n'attend
      // plus. Le remettre à `null` ici ne libérerait rien et rendrait chaque
      // rechargement d'écran plus lent d'un tour de boucle.
      poserLEtat(ETAT_INITIAL);
    };
    // `jeton`, `correspondances` et les préférences sont volontairement hors des
    // dépendances : voir l'en-tête de cet effet. Le jeton passe par sa référence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif, coursId, adresse, visioDisponible]);

  /**
   * **Couper ou rallumer son propre flux, et la préférence suit** (Q419, Q420).
   *
   * « Couper ou rallumer pendant un cours **met à jour cette préférence** » : c'est donc
   * ce geste-ci qui écrit, et **jamais** la coupure imposée par le tuteur, qui n'arrive
   * pas par ici (DD-16). Et « couper son propre flux **ne laisse aucune ligne** » : ce
   * geste ne touche aucune route de trace.
   */
  const basculerLeMicro = useCallback(async () => {
    const piece = salle.current;
    if (piece === null) return;
    const voulu = !piece.localParticipant.isMicrophoneEnabled;
    try {
      await piece.localParticipant.setMicrophoneEnabled(voulu);
      preference.onBascule('micro', voulu);
    } catch {
      poserLEtat((precedent) => ({ ...precedent, materielRefuse: true }));
    }
  }, [preference]);

  const basculerLaCamera = useCallback(async () => {
    const piece = salle.current;
    if (piece === null) return;
    const voulu = !piece.localParticipant.isCameraEnabled;
    try {
      await piece.localParticipant.setCameraEnabled(voulu);
      preference.onBascule('camera', voulu);
    } catch {
      poserLEtat((precedent) => ({ ...precedent, materielRefuse: true }));
    }
  }, [preference]);

  /**
   * **Partager son écran, et il se SUBSTITUE à l'image** (Q423, DD-17).
   *
   * « Une personne publie son visage ou son écran, **jamais les deux** », et « le nombre
   * de flux publiés ne change donc pas quand quelqu'un partage ». Cesser de partager
   * rétablit la caméra **selon la préférence**, et non allumée d'office : la rallumer
   * sans le demander exposerait une image que la personne avait coupée.
   *
   * **La préférence n'est pas touchée par ce geste** : le partage n'est pas une
   * préférence de la personne, et le modèle n'en porte pas.
   */
  const basculerLEcran = useCallback(async () => {
    const piece = salle.current;
    if (piece === null) return;
    const voulu = !piece.localParticipant.isScreenShareEnabled;
    try {
      if (voulu) {
        await piece.localParticipant.setScreenShareEnabled(true);
        await piece.localParticipant.setCameraEnabled(false);
      } else {
        await piece.localParticipant.setScreenShareEnabled(false);
        await piece.localParticipant.setCameraEnabled(preferenceCamera);
      }
    } catch {
      // Un refus de partage d'écran par le navigateur n'est pas une panne du produit :
      // « ce qui se partage est choisi par la machine de la personne », y compris rien.
      if (salle.current !== null) relire(salle.current);
    }
  }, [preferenceCamera, relire]);

  return {
    /**
     * **`visioDisponible` est recollé ici, dérivé de la réponse du droit.**
     *
     * C'est le seul champ de `EtatDeLaVisio` qui ne soit pas tenu localement, et son
     * motif est écrit sur le type : il vient du serveur, donc il ne se recopie pas.
     */
    etat: { ...etat, visioDisponible } satisfies EtatDeLaVisio,
    /** Vrai quand le droit n'a pas encore répondu : l'écran montre son squelette. */
    enChargement: droit.isPending && actif && coursId !== null,
    erreur: droit.error,
    basculerLeMicro,
    basculerLaCamera,
    basculerLEcran,
  };
}

/**
 * **Le seul endroit où une piste du fournisseur est touchée.**
 *
 * Isolé dans une fonction pour que le type du SDK ne remonte pas dans la signature de
 * `VignetteDeVisio` : ce que l'écran reçoit est une fonction, et c'est ce qui fait que
 * D-003 tient jusqu'au composant.
 */
function brancheur(
  piste: NonNullable<TrackPublication['track']> | LocalTrack | RemoteTrack,
): (element: HTMLMediaElement) => () => void {
  return (element) => {
    piste.attach(element);
    return () => piste.detach(element);
  };
}
